<template>
  <section class="city-lab policy-teacher-assist">
    <nav class="lab-crumb">政策平台 / <b>教师辅助功能</b></nav>
    <operation-message :value="notice" :type="failed ? 'error' : 'success'" @consumed="notice = ''" />
    <router-link v-if="copiedCase && !failed" class="btn small" :to="{ path: '/cases', query: { module: 'policy', view: 'custom', customId: copiedCase.id } }">配置副本</router-link>
    <lab-panel title="① 一键切换学生视角">
      <div class="lab-note policy-assist-intro">教师可一键切换至学生视角，沉浸式体验完整实训流程，用于课前测试案例。体验数据独立存储，不影响学生成绩。</div>
      <div v-if="loading" class="lab-empty" role="status">正在加载政策案例…</div>
      <div v-else-if="loadFailed" class="lab-empty">政策案例加载失败。<button class="btn small" :disabled="busy" @click="load()">重新加载</button></div>
      <div v-else-if="cases.length" class="policy-assist-cases">
        <article v-for="item in visibleCases" :key="item.kind + ':' + item.id" class="lab-card policy-assist-case" :data-case-id="item.id">
          <div class="lab-card-b">
            <h4>{{ item.name }}</h4>
            <div class="policy-assist-meta">{{ item.caseType || '自定义政策' }} · {{ item.duration || 60 }} 分钟</div>
            <p v-for="(error, index) in caseErrors(item)" :key="index" class="policy-assist-validation">{{ error }}</p>
            <button class="btn primary small" :disabled="busy || !!experienceReason(item)" :title="experienceReason(item) || (busy ? '正在处理操作，请稍候' : '')" :aria-busy="experienceId === item.id" @click="experience(item)">{{ experienceId === item.id ? '正在进入…' : '以学生视角体验' }}</button>
          </div>
        </article>
        <list-pagination :page="casePage" :page-size="casePageSize" :total="cases.length" @change="casePage = $event" />
      </div>
      <div v-else class="lab-empty">暂无可体验的政策案例</div>
    </lab-panel>
    <lab-panel title="② 教师之间案例资源共享">
      <div class="lab-table-wrap" :aria-busy="loading">
        <table class="lab-table policy-assist-shared-table">
          <thead><tr><th>案例名称</th><th>创建人</th><th class="policy-assist-number">措施数</th><th>创建时间</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-if="loading"><td colspan="5" class="lab-empty">正在加载共享案例…</td></tr>
            <tr v-else-if="loadFailed"><td colspan="5" class="lab-empty">共享案例加载失败，请重新加载。</td></tr>
            <template v-else>
              <tr v-for="item in visibleSharedCases" :key="item.id" :data-case-id="item.id">
                <td>{{ item.name }}</td><td>{{ item.authorName || '—' }}</td><td class="policy-assist-number">{{ item.measureCount == null ? '—' : item.measureCount }}</td><td>{{ date(item.createdAt) }}</td>
                <td><button v-if="item.canCopy" class="btn small" :disabled="busy || !!copyReason(item)" :title="copyReason(item) || (busy ? '正在处理操作，请稍候' : '')" :aria-busy="copyId === item.id" @click="copy(item)">{{ copyId === item.id ? '正在复制…' : '复制到我的案例' }}</button><router-link v-else class="btn small" :to="{ path: '/cases', query: { module: 'policy', view: 'custom', customId: item.id } }">查看详情</router-link></td>
              </tr>
              <tr v-if="!sharedCases.length"><td colspan="5" class="lab-empty">暂无其他教师共享的案例</td></tr>
            </template>
          </tbody>
        </table>
        <list-pagination v-if="sharedCases.length" :page="sharedPage" :page-size="sharedPageSize" :total="sharedCases.length" @change="sharedPage = $event" />
      </div>
    </lab-panel>
    <details v-if="legacyNotice || notes.length" class="policy-assist-notes">
      <summary>体验与共享说明</summary>
      <p v-if="legacyNotice">{{ legacyNotice }}</p>
      <p v-for="(note, index) in notes" :key="index">{{ note }}</p>
    </details>
  </section>
</template>

<script>
import LabPanel from '../components/NetworkLabPanel.vue'
import ListPagination from '../components/ListPagination.vue'
import { api, errorText } from '../services/api'
import '../assets/policy-teacher-assist.css'

const base = '/policy/teacher-assist'
const requestId = () => 'policy-assist-' + (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2))
const hasRevision = item => item.revision != null && item.revision !== ''

// The assist endpoint supplies current policy sources, never published version lists.
function currentCases (items) {
  const cases = new Map()
  const version = value => Number(value.kind === 'CUSTOM' ? value.revision : value.versionNo) || 0
  for (const item of Array.isArray(items) ? items : []) {
    if (!item.id || !['BUILTIN', 'CUSTOM'].includes(item.kind)) continue
    const key = item.kind + ':' + item.id
    const previous = cases.get(key)
    if (!previous || version(item) > version(previous)) cases.set(key, item)
  }
  return Array.from(cases.values())
}

export default {
  name: 'PolicyTeacherAssist',
  components: { LabPanel, ListPagination },
  data () {
    return { cases: [], sharedCases: [], casePage: 1, casePageSize: 9, sharedPage: 1, sharedPageSize: 10, canExperience: false, canCopy: false, notes: [], legacyNotice: '', loading: true, loadFailed: false, loadVersion: 0, disposed: false, notice: '', failed: false, experienceId: '', copyId: '', copyRequests: {}, copiedCase: null }
  },
  computed: {
    busy () { return !!(this.experienceId || this.copyId) },
    visibleCases () { const start = (this.casePage - 1) * this.casePageSize; return this.cases.slice(start, start + this.casePageSize) },
    visibleSharedCases () { const start = (this.sharedPage - 1) * this.sharedPageSize; return this.sharedCases.slice(start, start + this.sharedPageSize) }
  },
  created () { this.load() },
  beforeDestroy () { this.disposed = true; this.loadVersion++ },
  methods: {
    ensureCasePage () { const totalPages = Math.max(1, Math.ceil(this.cases.length / this.casePageSize)); if (this.casePage > totalPages) this.casePage = totalPages },
    ensureSharedPage () { const totalPages = Math.max(1, Math.ceil(this.sharedCases.length / this.sharedPageSize)); if (this.sharedPage > totalPages) this.sharedPage = totalPages },
    async load (preserveNotice = false) {
      if (this.disposed) return false
      const version = ++this.loadVersion
      this.loading = true
      this.loadFailed = false
      if (!preserveNotice) { this.notice = ''; this.failed = false }
      try {
        const data = await api.get(base)
        if (version !== this.loadVersion || this.disposed) return false
        if (!data || !Array.isArray(data.cases) || !Array.isArray(data.sharedCases)) throw new Error('政策案例列表返回不完整，请重新加载')
        this.cases = currentCases(data.cases)
        this.sharedCases = data.sharedCases
        this.ensureCasePage(); this.ensureSharedPage()
        this.canExperience = data.canExperience === true
        this.canCopy = data.canCopy === true
        this.notes = (Array.isArray(data.notes) ? data.notes : [data.notes]).filter(note => typeof note === 'string' && note)
        this.legacyNotice = data.legacyNotice || ''
        return true
      } catch (error) {
        if (version !== this.loadVersion || this.disposed) return false
        this.cases = []; this.sharedCases = []; this.ensureCasePage(); this.ensureSharedPage(); this.canExperience = false; this.canCopy = false
        this.notes = []; this.legacyNotice = ''; this.loadFailed = true
        this.notice = (preserveNotice && this.notice ? this.notice + '；' : '') + '列表加载失败：' + errorText(error)
        this.failed = true
        return false
      } finally {
        if (version === this.loadVersion && !this.disposed) this.loading = false
      }
    },
    caseErrors (item) {
      const errors = item.validationErrors || []
      return (Array.isArray(errors) ? errors : [errors]).map(error => typeof error === 'string' ? error : (error && (error.message || error.error)) || '案例配置校验未通过').filter(Boolean)
    },
    experienceReason (item) {
      if (this.loading || this.loadFailed) return '请等待政策案例列表成功加载'
      if (!this.canExperience) return '当前账号未获政策案例体验权限'
      const errors = this.caseErrors(item)
      if (errors.length) return '案例配置校验未通过：' + errors.join('；')
      if (item.canExperience !== true) return '该案例当前不可体验，请检查配置或刷新列表'
      if (item.kind === 'CUSTOM' && !hasRevision(item)) return '案例修订信息缺失，请重新加载'
      return ''
    },
    copyReason (item) {
      if (this.loading || this.loadFailed) return '请等待共享案例列表成功加载'
      if (!this.canCopy) return '当前账号未获共享案例复制权限'
      if (item.canCopy !== true) return '该共享案例当前不可复制，可能已取消共享或无访问权限'
      if (!hasRevision(item)) return '共享案例修订信息缺失，请重新加载'
      return ''
    },
    async experience (item) {
      if (this.busy || this.loading || this.disposed) return
      const source = this.cases.find(value => value.id === item.id && value.kind === item.kind)
      const reason = source ? this.experienceReason(source) : '案例已变更，请重新加载'
      if (reason) { this.notice = reason; this.failed = true; return }
      this.experienceId = source.id
      this.notice = ''; this.failed = false; this.copiedCase = null
      try {
        const body = { requestId: requestId() }
        if (source.kind === 'CUSTOM') body.revision = source.revision
        const data = await api.post(base + '/cases/' + encodeURIComponent(source.id) + '/experience', body)
        if (this.disposed) return
        if (!data || !data.run || !data.run.id) throw new Error('未返回有效体验运行，请重新加载后重试')
        await this.$router.push({ name: 'policy-run', params: { runId: data.run.id }, query: { mode: 'EXPERIENCE' } })
      } catch (error) {
        if (!this.disposed) await this.actionFailed(error, '进入体验')
      } finally {
        if (!this.disposed) this.experienceId = ''
      }
    },
    async copy (item) {
      if (this.busy || this.loading || this.disposed) return
      const source = this.sharedCases.find(value => value.id === item.id)
      const reason = source ? this.copyReason(source) : '共享案例已变更，请重新加载'
      if (reason) { this.notice = reason; this.failed = true; return }
      this.copyId = source.id
      this.failed = false; this.copiedCase = null
      const pending = this.copyRequests[source.id]
      const body = pending || { revision: source.revision, requestId: requestId() }
      this.$set(this.copyRequests, source.id, body)
      this.notice = pending ? '正在核对上次复制结果，重试将复用同一请求。' : ''
      try {
        const data = await api.post(base + '/shared-cases/' + encodeURIComponent(source.id) + '/copy', body)
        if (this.disposed) return
        if (!data || !data.case || !data.case.id) throw new Error('未返回有效副本，请重试以确认本次复制结果')
        this.$delete(this.copyRequests, source.id)
        this.copiedCase = data.case
        this.notice = '已将“' + data.case.name + '”复制到我的案例。'
        await this.load(true)
      } catch (error) {
        if (this.disposed) return
        const status = error.response && error.response.status
        // A lost response may already have created the copy; keep its request for retry.
        if (status === 403 || status === 409) this.$delete(this.copyRequests, source.id)
        await this.actionFailed(error, '复制案例')
        if (!status || status === 408 || status >= 500) this.notice += '；复制结果尚未确认，请重试，重试不会重复创建副本。'
      } finally {
        if (!this.disposed) this.copyId = ''
      }
    },
    async actionFailed (error, action) {
      this.notice = action + '失败：' + errorText(error)
      this.failed = true
      const status = error.response && error.response.status
      if (status === 403 || status === 409) await this.load(true)
    },
    date (value) {
      if (!value) return '—'
      const date = new Date(value)
      if (!Number.isFinite(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, '0')
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds())
    }
  }
}
</script>
